import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Select from 'react-select'
import { ICommissionRule } from '../../core/response.types'
import {
  CURRENCY_LABELS,
  CashboxKind,
  CommissionCurrency,
  CommissionDirection,
  CommissionFeeKind,
  DIRECTION_LABELS,
  FEE_KIND_OPTIONS,
  KIND_LABELS,
  MONEY_PATTERN,
  feeKindFieldState,
} from '../../core/consts'
import {
  CreateCommissionRulePayload,
  UpdateCommissionRulePayload,
} from '../../core/requests.types'
import MerchantFilter from '../../../../../../common/filter/MerchantFilter'
import PaymentMethodsFilter from '../../../../../../common/filter/PaymentMethodsFilter'
import { SelectStyles } from '../../../../../../../components/Select'
import { useThemeMode } from '../../../../../../../_metronic/partials'
import { getCashBoxesLookup } from '../../../../../../common/core/_requests'

interface Props {
  show:     boolean
  rule:     ICommissionRule | null
  onClose:  () => void
  onSubmit: (
    payload: CreateCommissionRulePayload | UpdateCommissionRulePayload,
    mode: 'create' | 'edit',
  ) => Promise<void>
}

interface FormValues {
  direction:     string
  currency:      string
  merchantToken: string
  cashBoxId:     number | ''
  cashboxKind:   string
  paymentMethod: string
  amountMin:     string
  amountMax:     string
  bps:           string
  feeKind:       CommissionFeeKind | ''
  fixed:         string
  minFee:        string
  maxFee:        string
}

const blank: FormValues = {
  direction: '', currency: '', merchantToken: '', cashBoxId: '',
  cashboxKind: '', paymentMethod: '',
  amountMin: '', amountMax: '', bps: '', feeKind: '', fixed: '',
  minFee: '', maxFee: '',
}

const moneyOpt = Yup.string().nullable().test(
  'money4',
  'до 4 знаков после запятой',
  (v) => v == null || v === '' || MONEY_PATTERN.test(v),
)
const moneyReq = Yup.string()
  .required('Обязательно')
  .matches(MONEY_PATTERN, 'до 4 знаков после запятой')

const schema = Yup.object().shape({
  direction: Yup.string().required('Обязательно'),
  currency:  Yup.string().required('Обязательно'),
  bps:       Yup.number()
    .typeError('Число')
    .min(0, '>= 0')
    .max(10000, '<= 10000')
    .required('Обязательно'),
  feeKind:   Yup.string()
    .oneOf(['CHARGE', 'REBATE'], 'Выберите значение')
    .required('Обязательно'),
  fixed:     moneyReq,
  amountMin: moneyOpt,
  amountMax: moneyOpt.test('amountRange', 'amountMax >= amountMin', function (v) {
    const min = this.parent.amountMin
    return !v || !min || parseFloat(v) >= parseFloat(min)
  }),
  minFee:    moneyOpt,
  maxFee:    moneyOpt.test('feeRange', 'maxFee >= minFee', function (v) {
    const min = this.parent.minFee
    return !v || !min || parseFloat(v) >= parseFloat(min)
  }),
  cashboxKind: Yup.string().nullable().test(
    'exclusive',
    'Выберите либо кассу, либо тип кассы',
    function (v) {
      return !v || !this.parent.cashBoxId
    },
  ),
})

const toFormValues = (r: ICommissionRule | null): FormValues => {
  if (!r) return blank
  return {
    direction:     r.direction,
    currency:      r.currency,
    merchantToken: r.merchantToken ?? '',
    cashBoxId:     r.cashBoxId ?? '',
    cashboxKind:   r.cashboxKind ?? '',
    paymentMethod: r.paymentMethod ?? '',
    amountMin:     r.amountMin ?? '',
    amountMax:     r.amountMax ?? '',
    bps:           String(r.bps),
    feeKind:       r.feeKind ?? 'CHARGE',
    fixed:         r.fixed,
    minFee:        r.minFee ?? '',
    maxFee:        r.maxFee ?? '',
  }
}

const inputClass = (touched?: boolean, error?: string) =>
  `form-control form-control-solid${touched && error ? ' is-invalid' : ''}`

const selectClass = inputClass

const directionOptions = Object.entries(DIRECTION_LABELS).map(([value, label]) => ({ value, label }))
const currencyOptions  = Object.entries(CURRENCY_LABELS).map(([value, label]) => ({ value, label }))
const kindOptions      = Object.entries(KIND_LABELS).map(([value, label]) => ({ value, label }))

const CommissionRuleModal: React.FC<Props> = ({ show, rule, onClose, onSubmit }) => {
  const locked = !!rule && (rule.immutable || rule.isDefault)
  const mode: 'create' | 'edit' = rule ? 'edit' : 'create'
  const { mode: theme } = useThemeMode()
  const selectStyles = SelectStyles(theme)

  const [cashBoxOptions, setCashBoxOptions] = useState<{ value: number; label: string }[]>([])
  useEffect(() => {
    let cancelled = false
    getCashBoxesLookup()
      .then((resp: any) => {
        if (cancelled) return
        setCashBoxOptions(resp?.data?.data ?? [])
      })
      .catch(() => setCashBoxOptions([]))
    return () => { cancelled = true }
  }, [])

  const formik = useFormik<FormValues>({
    initialValues: toFormValues(rule),
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (vals, helpers) => {
      const payload = {
        direction:     vals.direction as CommissionDirection,
        currency:      vals.currency  as CommissionCurrency,
        merchantToken: vals.merchantToken || null,
        cashBoxId:     vals.cashBoxId  === '' ? null : Number(vals.cashBoxId),
        cashboxKind:   (vals.cashboxKind || null) as CashboxKind | null,
        paymentMethod: vals.paymentMethod || null,
        amountMin:     vals.amountMin    || null,
        amountMax:     vals.amountMax    || null,
        bps:           Number(vals.bps),
        feeKind:       vals.feeKind as CommissionFeeKind,
        fixed:         vals.fixed,
        minFee:        vals.minFee || null,
        maxFee:        vals.maxFee || null,
      }
      try {
        await onSubmit(payload, mode)
      } catch (e: any) {
        const fieldErrors = e?.response?.data?.errors as Record<string, string> | undefined
        if (fieldErrors) {
          Object.entries(fieldErrors).forEach(([k, v]) => helpers.setFieldError(k, v))
        }
      }
    },
  })

  // Состояние поля «Кто платит комиссию» — в core/consts.ts, чтобы три его
  // ветки покрывались юнит-тестом без прогона react-select через jsdom.
  const feeKindState = feeKindFieldState(formik.values.direction)

  const { setFieldValue } = formik
  const currentFeeKind = formik.values.feeKind
  const forcedFeeKind = feeKindState.forcedValue
  useEffect(() => {
    if (forcedFeeKind !== null && currentFeeKind !== forcedFeeKind) {
      setFieldValue('feeKind', forcedFeeKind)
    }
  }, [forcedFeeKind, currentFeeKind, setFieldValue])

  return (
    <Modal show={show} onHide={onClose} size='xl' backdrop={formik.isSubmitting ? 'static' : true} centered>
      <div className='modal-header'>
        <h2 className='modal-title'>
          {mode === 'create'
            ? 'Создать правило'
            : locked
              ? 'Правило (только просмотр)'
              : 'Редактировать правило'}
        </h2>
      </div>
      <div className='modal-body'>
        <form onSubmit={formik.handleSubmit} noValidate>
          <div className='row g-3'>
            <div className='col-md-6'>
              <label className='form-label fw-bold'>Направление *</label>
              <Select
                isDisabled={locked}
                isClearable
                options={directionOptions}
                value={directionOptions.find((o) => o.value === formik.values.direction) ?? null}
                onChange={(opt) => formik.setFieldValue('direction', (opt as any)?.value ?? '')}
                onBlur={() => formik.setFieldTouched('direction', true)}
                placeholder='Выберите направление'
                styles={selectStyles}
              />
              <small className='text-muted'>* Operation direction: pay-in/pay-out/matching, мерчант / касса</small>
              {formik.touched.direction && formik.errors.direction && (
                <div className='invalid-feedback d-block'>{formik.errors.direction}</div>
              )}
            </div>
            <div className='col-md-6'>
              <label className='form-label fw-bold'>Валюта *</label>
              <Select
                isDisabled={locked}
                isClearable
                options={currencyOptions}
                value={currencyOptions.find((o) => o.value === formik.values.currency) ?? null}
                onChange={(opt) => formik.setFieldValue('currency', (opt as any)?.value ?? '')}
                onBlur={() => formik.setFieldTouched('currency', true)}
                placeholder='Выберите валюту'
                styles={selectStyles}
              />
              {formik.touched.currency && formik.errors.currency && (
                <div className='invalid-feedback d-block'>{formik.errors.currency}</div>
              )}
            </div>
          </div>

          <div className='row g-3 mt-1'>
            <div className='col-md-6'>
              <label className='form-label fw-bold'>Кто платит комиссию *</label>
              <Select
                isDisabled={locked || feeKindState.disabled}
                options={FEE_KIND_OPTIONS}
                value={FEE_KIND_OPTIONS.find((o) => o.value === formik.values.feeKind) ?? null}
                onChange={(opt) => formik.setFieldValue('feeKind', (opt as any)?.value ?? '')}
                onBlur={() => formik.setFieldTouched('feeKind', true)}
                placeholder='Выберите, кто платит'
                styles={selectStyles}
              />
              <small className='text-muted'>{feeKindState.hint}</small>
              {formik.touched.feeKind && formik.errors.feeKind && (
                <div className='invalid-feedback d-block'>{formik.errors.feeKind}</div>
              )}
            </div>
          </div>

          <div className='row g-3 mt-1'>
            <div className='col-md-4'>
              <label className='form-label fw-bold'>Мерчант</label>
              <MerchantFilter
                value={formik.values.merchantToken ? [formik.values.merchantToken] : []}
                changeSelect={(d) => formik.setFieldValue('merchantToken', (d.value as string[])[0] ?? '')}
              />
            </div>
            <div className='col-md-4'>
              <label className='form-label fw-bold'>Касса</label>
              <Select
                isDisabled={locked || !!formik.values.cashboxKind}
                isClearable
                options={cashBoxOptions}
                value={cashBoxOptions.find((o) => o.value === formik.values.cashBoxId) ?? null}
                onChange={(opt) => formik.setFieldValue('cashBoxId', (opt as any)?.value ?? '')}
                placeholder='Выберите кассу'
                styles={selectStyles}
              />
              <small className='text-muted'>Взаимоисключающе с типом кассы</small>
            </div>
            <div className='col-md-4'>
              <label className='form-label fw-bold'>Тип кассы</label>
              <Select
                isDisabled={locked || formik.values.cashBoxId !== ''}
                isClearable
                options={kindOptions}
                value={kindOptions.find((o) => o.value === formik.values.cashboxKind) ?? null}
                onChange={(opt) => formik.setFieldValue('cashboxKind', (opt as any)?.value ?? '')}
                placeholder='Выберите тип'
                styles={selectStyles}
              />
              {formik.touched.cashboxKind && formik.errors.cashboxKind && (
                <div className='invalid-feedback d-block'>{formik.errors.cashboxKind}</div>
              )}
            </div>
          </div>

          <div className='row g-3 mt-1'>
            <div className='col-md-6'>
              <label className='form-label fw-bold'>Платёжный метод</label>
              <PaymentMethodsFilter
                value={formik.values.paymentMethod ? [formik.values.paymentMethod] : []}
                changeSelect={(d) => formik.setFieldValue('paymentMethod', (d.value as string[])[0] ?? '')}
              />
            </div>
          </div>

          <div className='row g-3 mt-1'>
            <div className='col-md-6'>
              <label className='form-label fw-bold'>Сумма от</label>
              <input
                className={inputClass(formik.touched.amountMin, formik.errors.amountMin)}
                {...formik.getFieldProps('amountMin')}
                disabled={locked}
                placeholder='100.0000'
              />
              <small className='text-muted'>Минимальная сумма операции, к которой применимо правило</small>
              {formik.touched.amountMin && formik.errors.amountMin && (
                <div className='invalid-feedback d-block'>{formik.errors.amountMin}</div>
              )}
            </div>
            <div className='col-md-6'>
              <label className='form-label fw-bold'>Сумма до</label>
              <input
                className={inputClass(formik.touched.amountMax, formik.errors.amountMax)}
                {...formik.getFieldProps('amountMax')}
                disabled={locked}
                placeholder='10000.0000'
              />
              <small className='text-muted'>Максимальная сумма операции</small>
              {formik.touched.amountMax && formik.errors.amountMax && (
                <div className='invalid-feedback d-block'>{formik.errors.amountMax}</div>
              )}
            </div>
          </div>

          <div className='row g-3 mt-1'>
            <div className='col-md-6'>
              <label className='form-label fw-bold'>Процент (bps) *</label>
              <input
                className={inputClass(formik.touched.bps, formik.errors.bps)}
                type='number'
                {...formik.getFieldProps('bps')}
                disabled={locked}
              />
              <small className='text-muted'>* Базисные пункты: 1 bps = 0.01%. Диапазон 0..10000 (0..100%). Всегда положительное число — направление задаётся полем «Кто платит комиссию»</small>
              {formik.touched.bps && formik.errors.bps && (
                <div className='invalid-feedback d-block'>{formik.errors.bps}</div>
              )}
            </div>
            <div className='col-md-6'>
              <label className='form-label fw-bold'>Фиксированная часть *</label>
              <input
                className={inputClass(formik.touched.fixed, formik.errors.fixed)}
                {...formik.getFieldProps('fixed')}
                disabled={locked}
                placeholder='0.0000'
              />
              <small className='text-muted'>Постоянная составляющая комиссии, добавляется к процентной</small>
              {formik.touched.fixed && formik.errors.fixed && (
                <div className='invalid-feedback d-block'>{formik.errors.fixed}</div>
              )}
            </div>
          </div>

          <div className='row g-3 mt-1'>
            <div className='col-md-6'>
              <label className='form-label fw-bold'>Минимальная комиссия</label>
              <input
                className={inputClass(formik.touched.minFee, formik.errors.minFee)}
                {...formik.getFieldProps('minFee')}
                disabled={locked}
              />
              {formik.touched.minFee && formik.errors.minFee && (
                <div className='invalid-feedback d-block'>{formik.errors.minFee}</div>
              )}
            </div>
            <div className='col-md-6'>
              <label className='form-label fw-bold'>Максимальная комиссия</label>
              <input
                className={inputClass(formik.touched.maxFee, formik.errors.maxFee)}
                {...formik.getFieldProps('maxFee')}
                disabled={locked}
              />
              {formik.touched.maxFee && formik.errors.maxFee && (
                <div className='invalid-feedback d-block'>{formik.errors.maxFee}</div>
              )}
            </div>
          </div>

          <div className='d-flex justify-content-end gap-2 mt-4'>
            <button type='button' className='btn btn-light' onClick={onClose} disabled={formik.isSubmitting}>
              Закрыть
            </button>
            {!locked && (
              <button type='submit' className='btn btn-primary' disabled={formik.isSubmitting}>
                {formik.isSubmitting && <span className='spinner-border spinner-border-sm me-2' />}
                {mode === 'create' ? 'Создать' : 'Сохранить'}
              </button>
            )}
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default CommissionRuleModal
